// boxGizmo.ts — the geometry behind the Viewer's 3D box tool (guided
// segmentation, ARCHITECTURE.md §10). A box is axis-aligned in openscan's
// Z-up world (the marker sheet is z=0). It is drawn as a wireframe with a
// handle at each face centre and a move handle at its centre. Dragging a
// face handle slides that face along its axis: the new position is the point
// on the axis line closest to the pointer ray, so it tracks the pointer from
// any view angle. The move handle slides the box in the horizontal plane
// through its centre. A new box is drawn by dragging across the sheet: the
// two ray–plane (z=0) hits are opposite corners of its footprint, and its
// height fits the tallest splat standing on that footprint.

import type { SplatCloud } from './format';
import { projectToScreen, vec3Add, vec3Dot, vec3Scale, vec3Sub } from './math';
import type { Mat4, Vec3 } from './math';
import type { Ray } from './pick';

export interface Box3 {
  min: Vec3;
  max: Vec3;
}

export type BoxHandle = { kind: 'face'; axis: 0 | 1 | 2; side: 'min' | 'max' } | { kind: 'move' };

// The thinnest a dragged box may get (the server refuses < 1 mm).
export const MIN_BOX_EDGE_M = 0.002;
// Headroom above the tallest splat when a drawn box fits its height.
export const BOX_TOP_MARGIN_M = 0.003;
// Height of a drawn box whose footprint holds no splat.
export const DEFAULT_BOX_HEIGHT_M = 0.05;
// How near (CSS px) a press must land to grab a handle.
export const HANDLE_HIT_PX = 16;

// boxCorners lists the 8 corners; corner i takes max on axis k when bit k is set.
export function boxCorners(b: Box3): Vec3[] {
  const out: Vec3[] = [];
  for (let i = 0; i < 8; i++) {
    out.push([i & 1 ? b.max[0] : b.min[0], i & 2 ? b.max[1] : b.min[1], i & 4 ? b.max[2] : b.min[2]]);
  }
  return out;
}

// BOX_EDGES are the 12 corner pairs that differ in exactly one bit.
export const BOX_EDGES: ReadonlyArray<readonly [number, number]> = (() => {
  const e: [number, number][] = [];
  for (let i = 0; i < 8; i++) for (const bit of [1, 2, 4]) if (!(i & bit)) e.push([i, i | bit]);
  return e;
})();

export function boxCenter(b: Box3): Vec3 {
  return [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];
}

// handlePoint is the world position of a handle.
export function handlePoint(b: Box3, h: BoxHandle): Vec3 {
  const c = boxCenter(b);
  if (h.kind === 'move') return c;
  c[h.axis] = h.side === 'min' ? b.min[h.axis] : b.max[h.axis];
  return c;
}

export const BOX_HANDLES: readonly BoxHandle[] = [
  ...([0, 1, 2] as const).flatMap((axis) => (['min', 'max'] as const).map((side) => ({ kind: 'face' as const, axis, side }))),
  { kind: 'move' },
];

// hitHandle returns the handle whose projection is nearest (x, y) within
// maxPx (CSS px, viewport w×h), or null.
export function hitHandle(b: Box3, viewProj: Mat4, w: number, h: number, x: number, y: number, maxPx = HANDLE_HIT_PX): BoxHandle | null {
  let best: BoxHandle | null = null;
  let bestD = maxPx;
  for (const hd of BOX_HANDLES) {
    const p = projectToScreen(viewProj, handlePoint(b, hd), w, h);
    if (!p) continue;
    const d = Math.hypot(p[0] - x, p[1] - y);
    if (d <= bestD) {
      best = hd;
      bestD = d;
    }
  }
  return best;
}

// axisParam returns t such that origin + t·axis (axis a unit vector) is the
// point of that line closest to the ray, or null when the ray runs nearly
// along the axis (no stable answer).
export function axisParam(ray: Ray, origin: Vec3, axis: Vec3): number | null {
  const w0 = vec3Sub(origin, ray.origin);
  const b = vec3Dot(axis, ray.dir);
  const denom = 1 - b * b;
  if (denom < 1e-4) return null;
  return (b * vec3Dot(ray.dir, w0) - vec3Dot(axis, w0)) / denom;
}

// rayPlaneZ intersects the ray with the horizontal plane at height z, or
// null when it misses (parallel, or the plane is behind the camera).
export function rayPlaneZ(ray: Ray, z: number): Vec3 | null {
  if (Math.abs(ray.dir[2]) < 1e-6) return null;
  const s = (z - ray.origin[2]) / ray.dir[2];
  if (s <= 0) return null;
  return vec3Add(ray.origin, vec3Scale(ray.dir, s));
}

export function unitAxis(axis: 0 | 1 | 2): Vec3 {
  const a: Vec3 = [0, 0, 0];
  a[axis] = 1;
  return a;
}

// moveFace returns b with one face moved by d metres along its axis, kept at
// least MIN_BOX_EDGE_M from the opposite face.
export function moveFace(b: Box3, axis: 0 | 1 | 2, side: 'min' | 'max', d: number): Box3 {
  const out: Box3 = { min: [...b.min], max: [...b.max] };
  if (side === 'min') out.min[axis] = Math.min(b.min[axis] + d, b.max[axis] - MIN_BOX_EDGE_M);
  else out.max[axis] = Math.max(b.max[axis] + d, b.min[axis] + MIN_BOX_EDGE_M);
  return out;
}

export function translateBox(b: Box3, d: Vec3): Box3 {
  return { min: vec3Add(b.min, d), max: vec3Add(b.max, d) };
}

// fitHeight returns the top of the tallest splat (opacity ≥ minOpacity,
// centre above planeTolM) whose centre lies on the footprint, plus
// BOX_TOP_MARGIN_M; null when none does.
export function fitHeight(c: SplatCloud, x0: number, x1: number, y0: number, y1: number, planeTolM: number, minOpacity = 0.15): number | null {
  let top = -Infinity;
  for (let i = 0; i < c.count; i++) {
    const x = c.pos[i * 3], y = c.pos[i * 3 + 1], z = c.pos[i * 3 + 2];
    if (x < x0 || x > x1 || y < y0 || y > y1 || z < planeTolM || c.opacity[i] < minOpacity) continue;
    if (z > top) top = z;
  }
  return Number.isFinite(top) ? top + BOX_TOP_MARGIN_M : null;
}

// footprintBox is the box on the sheet with opposite footprint corners a and
// b, from z=0 up to height (at least MIN_BOX_EDGE_M on every axis).
export function footprintBox(a: Vec3, b: Vec3, height: number): Box3 {
  const lo = (k: number) => Math.min(a[k], b[k]);
  const hi = (k: number) => Math.max(Math.max(a[k], b[k]), lo(k) + MIN_BOX_EDGE_M);
  return { min: [lo(0), lo(1), 0], max: [hi(0), hi(1), Math.max(height, MIN_BOX_EDGE_M)] };
}
